import { Box, InputLabel, TextField } from "@mui/material";
import theme, { FONTS } from "../../constants/theme";
import { DatePicker, DatePickerSlotProps, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { scale } from "../../utils";
import React, { useState, useEffect } from "react";
import dayjs, { Dayjs } from "dayjs";
import updateLocale from 'dayjs/plugin/updateLocale';
import 'dayjs/locale/it';

dayjs.extend(updateLocale);
dayjs.updateLocale('it', {
  weekStart: 1,
});

interface Props {
  value: Dayjs | null;
  setValue: (values: Dayjs | null) => void;
}

const inputLabelStyle = {
  color: theme.palette.ceramic.contrastText,
  fontFamily: FONTS.MONTSERRAT,
  fontSize: scale(16),
  lineHeight: scale(20),
  fontWeight: 700,
  textTransform: "uppercase",
};

const textFieldSx = {
  "& .MuiInputLabel-root": {
    color: "black",
    fontWeight: 700,
    textTransform: "uppercase",
    fontSize: scale(14),
    lineHeight: scale(18),
    height: scale(40),
  },
  "& .MuiInputBase-root": {
    fontFamily: FONTS.MONTSERRAT,
    fontWeight: 300,
    fontSize: scale(16),
    color: "black",
    width: scale(299.8),
    boxSizing: "border-box",
    borderRadius: 0,
  },
  "& .MuiInputBase-input": {
    fontFamily: FONTS.MONTSERRAT,
    fontWeight: 300,
    fontSize: scale(16),
    color: "black",
    textAlign: "center",
    paddingTop: scale(7),
    paddingLeft: scale(8),
    paddingBottom: scale(6),
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
};

const slotProps: DatePickerSlotProps<true> = {
  openPickerButton: {
    sx: {
      '& svg': {
        fontSize: scale(20),
      },
    },
  },
  day: {
    sx: {
      backgroundColor: "#f0f0f0",
      color: "black",
      "&.Mui-selected": {
        backgroundColor: undefined,
        color: undefined,
      },
    },
  },
  popper: {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
    ],
    sx: {
      "& .MuiDayCalendar-weekDayLabel": {
        backgroundColor: "#f0f0f0",
        color: "black",
      },
      "& .MuiPaper-root": {
        backgroundColor: "#f0f0f0",
        color: "black",
        borderRadius: 0,
      },
    },
  },
};

const DateSelector = ({ value, setValue }: Props) => {
  const [dateError, setDateError] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
      return;
    }

    if (!value || !value.isValid()) {
      setDateError(true);
    } else {
      setDateError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleDateChange = (newValue: Dayjs | null) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", rowGap: '2px' }}>
      <InputLabel
        htmlFor={"date"}
        sx={[
          inputLabelStyle,
          { color: dateError ? theme.palette.error.main : theme.palette.ceramic.contrastText },
        ]}
      >
        {"Data"}
      </InputLabel>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
        <DatePicker
          label=""
          value={value ?? null}
          disablePast={true}
          onChange={handleDateChange}
          onClose={() => {
            if (!value || !value.isValid()) {
              setDateError(true);
            }
          }}
          enableAccessibleFieldDOMStructure={false}
          slotProps={slotProps}
          format="DD/MM/YYYY"
          slots={{
            textField: (props) => (
              <TextField
                {...props}
                variant="filled"
                placeholder="—/—/—"
                sx={textFieldSx}
                error={dateError}
              />
            ),
          }}
        />
      </LocalizationProvider>
    </Box>
  );
};

export default DateSelector;
